import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@clerk/react";
import { toast } from "sonner";
import { CheckCircle2, Download, Loader2, AlertTriangle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { makeApi } from "@/lib/api";
import { money } from "@/lib/format";
import { downloadBatch } from "@/pages/InvoicesList";

const PROFILES = [["direct", "Direct", "Paid to your own bank account"], ["factored", "Factored", "Paid to your factoring company"]];

export const BillSelectedDialog = ({ loads, open, onClose, onDone }) => {
  const { getToken } = useAuth();
  const [profile, setProfile] = useState("direct");
  const [remit, setRemit] = useState({});
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!open) return;
    setResult(null);
    makeApi(getToken).get("/settings/company").then(({ data }) => {
      setProfile(data.default_remit || "direct");
      setRemit({ direct: data.remit_direct, factored: data.remit_factored });
    }).catch(() => {});
  }, [open, getToken]);

  const total = loads.reduce((s, l) => s + (Number(l.billable_total ?? l.rate) || 0), 0);
  const bill = async () => {
    setBusy(true);
    try {
      const { data } = await makeApi(getToken).post("/invoices/batch", { load_ids: loads.map((l) => l.id), remit_profile: profile });
      setResult(data);
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not bill these loads");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && !busy && onClose()}>
      <DialogContent className="max-w-lg" data-testid="bill-selected-dialog">
        {!result ? (
          <>
            <DialogHeader>
              <DialogTitle>Bill {loads.length} load{loads.length === 1 ? "" : "s"}</DialogTitle>
              <DialogDescription>Each load gets its own invoice (freight + accessorials). They&apos;re created together as one batch.</DialogDescription>
            </DialogHeader>
            <div className="max-h-44 overflow-y-auto rounded-lg border border-slate-200 divide-y divide-slate-100 text-sm" data-testid="bill-selected-list">
              {loads.map((l) => (
                <div key={l.id} className="flex justify-between px-3 py-2"><span className="font-medium">#{l.load_number} <span className="font-normal text-slate-500">· {l.broker_name}</span></span><span className="tabular-nums">{money(l.billable_total ?? l.rate)}</span></div>
              ))}
            </div>
            <div className="flex justify-between text-sm font-semibold"><span>Total</span><span className="tabular-nums" data-testid="bill-selected-total">{money(total)}</span></div>
            <div>
              <div className="text-sm font-medium mb-2">Remit-to profile</div>
              <div className="grid grid-cols-2 gap-2">
                {PROFILES.map(([k, l, s]) => (
                  <button key={k} type="button" onClick={() => setProfile(k)} aria-pressed={profile === k}
                    className={`rounded-xl border-2 p-3 text-left transition-colors ${profile === k ? "border-[#002FA7] bg-blue-50" : "border-slate-200 hover:bg-slate-50"}`} data-testid={`bill-profile-${k}`}>
                    <div className="text-sm font-semibold">{l}</div><div className="text-[11px] text-slate-500">{s}</div>
                  </button>
                ))}
              </div>
              {!remit[profile] && <p className="mt-2 text-xs text-amber-800 flex items-center gap-1" data-testid="bill-remit-missing"><AlertTriangle className="w-3.5 h-3.5" /> No {profile} remit-to details yet. <Link to="/admin/settings" className="underline font-medium">Add them in Settings</Link></p>}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
              <Button onClick={bill} disabled={busy || !loads.length} className="bg-[#002FA7] hover:bg-[#001A75]" data-testid="bill-confirm-btn">
                {busy && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}Create {loads.length} invoice{loads.length === 1 ? "" : "s"}
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-emerald-600" /> {result.count} invoice{result.count === 1 ? "" : "s"} created</DialogTitle>
              <DialogDescription data-testid="bill-result-summary">Total {money(result.total)} · {result.remit_profile === "factored" ? "Factored" : "Direct"} remit-to</DialogDescription>
            </DialogHeader>
            {result.created.length > 0 && (
              <div className="max-h-40 overflow-y-auto rounded-lg border border-slate-200 divide-y divide-slate-100 text-sm" data-testid="bill-result-created">
                {result.created.map((c) => (
                  <Link key={c.invoice_id} to={`/admin/invoices/${c.invoice_id}`} className="flex justify-between px-3 py-2 hover:bg-slate-50"><span><span className="font-medium text-[#002FA7]">{c.invoice_number}</span> · #{c.load_number}</span><span className="tabular-nums">{money(c.amount)}</span></Link>
                ))}
              </div>
            )}
            {result.skipped.length > 0 && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900" data-testid="bill-result-skipped">
                Skipped {result.skipped.length}: {result.skipped.map((s) => `#${s.load_number || "?"} (${s.reason})`).join(", ")}
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={onClose} data-testid="bill-result-close">Done</Button>
              {result.count > 0 && <Button onClick={() => downloadBatch(getToken, result.id)} className="bg-[#002FA7] hover:bg-[#001A75]" data-testid="bill-download-batch"><Download className="w-4 h-4 mr-1.5" /> Combined PDF (invoices + packets)</Button>}
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
